import React, { useCallback, useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';
import { useDisclosure } from '@withjoy/joykit';
import type { CatalogContextProps } from '../../state/Catalog';
import { AddCashFundDialog } from '../../../AddCashFund/AddCashFundDialog';
import { EditCashFundDialog } from '../../../AddCashFund/components/EditCashFundDialog/EditCashFundDialog';
import type { AddCashFundDialogProps, EditCashFundDialogProps } from '../../..';
import type { CatalogItemTile } from '../../Catalog.types';
import { CustomItemDataType } from '@graphql/generated-base';
import type { CookedProductCatalogItem } from '@apps/registry/common/selectors/ProductListSelector';
import { DEFAULT_CURRENCY_SYMBOL } from '@shared/utils/currency/constants';
import { addRendition } from '@shared/utils/photoRendition';
import { AddCashFundTelemetryProvider } from '../../../AddCashFund/AddCashFund.telemetry';
import type { PreviewAndEditCatalogCashItemDialogProps } from '../PreviewAndEditCatalogCashItemDialog';
import { PreviewAndEditCatalogCashItemDialog } from '../PreviewAndEditCatalogCashItemDialog';

const NOOP = () => {};

type MinimalAddCatalogCashFundDialogProps = Pick<EditCashFundDialogProps, 'fundType' | 'product' | 'isCharityFund' | 'itemPosition' | 'addMethodType'>;
type MinimalAddCustomCashFundDialogProps = Pick<AddCashFundDialogProps, 'fundType' | 'addMethodType'>;
type MinimalPreviewAndEditCashFundDialogProps = Pick<PreviewAndEditCatalogCashItemDialogProps, 'product' | 'itemPosition'>;

type openAddCatalogCashFundDialogPropsType = {
  addMethodType: MinimalAddCatalogCashFundDialogProps['addMethodType'];
  itemPosition: Maybe<number>;
  product: CatalogItemTile;
};

export type CashFundDialogContext = {
  openAddCatalogCashFundDialog: (props: openAddCatalogCashFundDialogPropsType) => void;
  openCustomCashFundDialog: (props: MinimalAddCustomCashFundDialogProps) => void;
  openPreviewAndEditCatalogCashItemDialog: (props: MinimalPreviewAndEditCashFundDialogProps) => void;
};

const [Provider, useCashFundDialogContext] = createContext<CashFundDialogContext>({ name: 'CashFundDialogProvider' });

interface CashFundDialogProviderProps
  extends Readonly<{
      children: React.ReactNode;
      telemetryCategoryOverride?: string;
    }>,
    Pick<CatalogContextProps, 'selectedCategory' | 'donationFundInfo' | 'eventId' | 'registryCurrency'> {}

/**
 * Previously existing logic, required for the EditCashFundDialog
 */
const cookCashFundProduct = (product: CatalogItemTile) => {
  const itemType = product.itemType === 'charity' ? CustomItemDataType.charity : CustomItemDataType.cash;
  const cookedProduct: CookedProductCatalogItem = {
    id: product.id,
    collectionDisplayInfos: product.collectionDisplayInfos,
    title: product.title,
    image: `${addRendition({ url: product.photos[0]?.url, renditionSize: 'small' })}`,
    requested: 1,
    numberPrice: product.price?.valueInMinorUnits ? product.price?.valueInMinorUnits / 100 : 0,
    currencyCode: product.price?.currency?.code || DEFAULT_CURRENCY_SYMBOL,
    donationFund: {
      fund: {
        goalMonetaryValue: {
          valueInMinorUnits: product.price?.valueInMinorUnits || 0,
          floatingPointDecimalString: product.price?.floatingPointDecimalString || '$0',
        },
        enforceSuggestedDonationAmount: false,
        /**
         * `url` is required to properly show charity links.
         * src/apps/registry/common/components/AddCashFund/AddCashFundDialog.controller.ts#LN185
         */
        url: product.productUrl || undefined,
      },
    },
  };

  return {
    itemType,
    cookedProduct,
  } as const;
};

/**
 * NOTE: THis must consume any Catalog context values as props
 */
export const CashFundDialogProvider = (props: CashFundDialogProviderProps) => {
  const { children, eventId, selectedCategory, donationFundInfo, registryCurrency } = props;
  /**
   * Transitive state -- holds static data that drives the
   */
  const [addCatalogCashFundDialogProps, setAddCatalogCashFundDialogProps] = useState<MinimalAddCatalogCashFundDialogProps | null>(null);
  const [addCustomCashFundDialogProps, setAddCustomCashFundDialogProps] = useState<MinimalAddCustomCashFundDialogProps | null>(null);
  const [previewAndEditCatalogCashFundDialogProps, setPreviewAndEditCatalogCashItemDialogProps] = useState<MinimalPreviewAndEditCashFundDialogProps | null>(null);
  const { isOpen: isAddCatalogCashFundDialogOpen, onOpen: _openAddCatalogCashFundDialog, onClose: closeAddCatalogCashFundDialog } = useDisclosure();
  const { isOpen: isCustomCashFundDialogOpen, onOpen: _openCustomCashFundDialog, onClose: closeCustomCashFundDialogOpen } = useDisclosure();
  const { isOpen: isPreviewAndEditCatalogCashItemDialogOpen, onOpen: openPreviewAndEditCatalogCashItemDialog, onClose: closePreviewAndEditCatalogCashItemDialog } = useDisclosure();

  const openCustomCashFundDialog = useCallback<CashFundDialogContext['openCustomCashFundDialog']>(
    ({ addMethodType, fundType }) => {
      setAddCustomCashFundDialogProps({
        fundType,
        addMethodType,
      });
      _openCustomCashFundDialog();
    },
    [_openCustomCashFundDialog],
  );

  const openAddCatalogCashFundDialog = useCallback<CashFundDialogContext['openAddCatalogCashFundDialog']>(
    ({ product, addMethodType, itemPosition }) => {
      closePreviewAndEditCatalogCashItemDialog();
      const { itemType, cookedProduct } = cookCashFundProduct(product);
      setAddCatalogCashFundDialogProps({
        addMethodType,
        itemPosition,
        product: cookedProduct,
        fundType: itemType,
        isCharityFund: itemType === CustomItemDataType.charity,
      });
      _openAddCatalogCashFundDialog();
    },
    [_openAddCatalogCashFundDialog, closePreviewAndEditCatalogCashItemDialog],
  );

  const wrappedOnPreviewAndEditCatalogCashItemDialog = useCallback<CashFundDialogContext['openPreviewAndEditCatalogCashItemDialog']>(
    args => {
      setPreviewAndEditCatalogCashItemDialogProps(args);
      openPreviewAndEditCatalogCashItemDialog();
    },
    [openPreviewAndEditCatalogCashItemDialog],
  );

  const ctx = useMemo<CashFundDialogContext>(() => {
    return { openAddCatalogCashFundDialog, openCustomCashFundDialog, openPreviewAndEditCatalogCashItemDialog: wrappedOnPreviewAndEditCatalogCashItemDialog };
  }, [openAddCatalogCashFundDialog, openCustomCashFundDialog, wrappedOnPreviewAndEditCatalogCashItemDialog]);

  return (
    <AddCashFundTelemetryProvider context={{ eventId }}>
      <Provider value={ctx}>
        {children}

        {/* Not ideal, but will change once we update EditDialogProvider. */}
        {addCatalogCashFundDialogProps && eventId ? (
          // Passing the same props as from here https://github.com/joylifeinc/joy-web/pull/3083/files#diff-022a2a19085db50cb71d3eefde3878b70f05fe2148a8b1ea4ac7daac6f33fe5fL112
          <EditCashFundDialog
            key={addCatalogCashFundDialogProps.product?.id}
            donationFundInfo={donationFundInfo}
            eventId={eventId}
            // Cannot "delete" a brand new cash fund.
            handleOnRemove={NOOP}
            hideDialog={closeAddCatalogCashFundDialog}
            isOpen={isAddCatalogCashFundDialogOpen}
            // setting this to true because you can only add new cash funds from catalog.
            isNewCashFund={true}
            isCatalogDialog={true}
            registryCurrency={registryCurrency}
            selectedCategory={selectedCategory}
            telemCategoryOverride={props.telemetryCategoryOverride}
            {...addCatalogCashFundDialogProps}
          />
        ) : null}
        {addCustomCashFundDialogProps && eventId ? (
          <AddCashFundDialog
            isOpen={isCustomCashFundDialogOpen}
            hideDialog={closeCustomCashFundDialogOpen}
            eventId={eventId || ''}
            donationFundInfo={donationFundInfo}
            selectedCategory={selectedCategory}
            registryCurrency={registryCurrency}
            itemPosition={null}
            {...addCustomCashFundDialogProps}
          />
        ) : null}
        {previewAndEditCatalogCashFundDialogProps && (
          <PreviewAndEditCatalogCashItemDialog
            isOpen={isPreviewAndEditCatalogCashItemDialogOpen}
            onClose={closePreviewAndEditCatalogCashItemDialog}
            editCurrentProduct={openAddCatalogCashFundDialog}
            registryCurrency={registryCurrency}
            {...previewAndEditCatalogCashFundDialogProps}
          />
        )}
      </Provider>
    </AddCashFundTelemetryProvider>
  );
};

export { useCashFundDialogContext };
